import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../api-client.config';
import { Category } from '../types';

const getCategories = (params: {
  offset: number;
  limit: number;
  languageCode: string;
}): Promise<{ categories: Category[] }> => api.get(`/categories`, { params });

interface UseGetCategoriesQueryOptions {
  offset?: number;
  limit?: number;
  languageCode?: string;
}

export const useGetCategoriesQuery = (
  options?: UseGetCategoriesQueryOptions
) => {
  const { offset = 0, limit = 200, languageCode = '' } = options || {};

  return useQuery({
    queryKey: ['categories', offset, limit, languageCode],
    queryFn: () => getCategories({ offset, limit, languageCode }),
    select: (data) => data.categories,
  });
};

export const useGetInfiniteCategoriesQuery = (
  options?: UseGetCategoriesQueryOptions
) => {
  const { limit = 200, languageCode = '' } = options || {};

  return useInfiniteQuery({
    queryKey: ['categories', languageCode],
    queryFn: ({ pageParam }) =>
      getCategories({ offset: pageParam * limit, limit, languageCode }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.categories?.length < limit
        ? undefined
        : allPages?.length;
    },
    initialPageParam: 0,
    select: (data) => {
      // Merge leaderboard data from each page into a single array.
      // Keep all other properties as they are on page 0.
      if (!data?.pages?.length) return null;
      const mergedCategories = data.pages.flatMap(
        (page) => page.categories || []
      );
      return mergedCategories;
    },
  });
};

export const useGetAllCategoriesQuery = (
  options?: UseGetCategoriesQueryOptions
) => {
  const { limit = 200, languageCode = '' } = options || {};

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['all-categories', languageCode],
    queryFn: ({ pageParam }) =>
      getCategories({ offset: pageParam * limit, limit, languageCode }),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.categories?.length < limit
        ? undefined
        : allPages?.length;
    },
    initialPageParam: 0,
  });

  // Automatically fetch all pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Merge all pages into a single array
  const categories = useMemo(() => {
    if (!data?.pages?.length) return [];
    return data.pages.flatMap((page) => page.categories || []);
  }, [data]);

  return {
    data: categories,
    isLoading: isLoading || (isFetching && categories.length === 0),
    isFetching,
    error,
  };
};
